import ActionType from '@/shared/enums/ActionType';
import EntityType from '@/shared/enums/EntityType';
import roundFloat from '@pkg/utils/numbers/roundFloat';
import actionExists from '../actionExists';
import addedActivitiesAction from '../actions/addedActivitiesAction';
import addedRolesAction from '../actions/addedRolesAction';
import removedActivitiesAction from '../actions/removedActivitiesAction';
import removedRolesAction from '../actions/removedRolesAction';
import updatedBudgetAction from '../actions/updatedBudgetAction';
import updatedFTEAction from '../actions/updatedFTEAction';
import updatedHoursAction from '../actions/updatedHoursAction';
import includesNewActivity from '../includesNewActivity';

export default function addImpactByImpactType({
  activity,
  impactMap,
  comparisonScenario,
  originalActivity,
  originalRole,
  role,
  scenario,
  showBudget,
}) {
  const impacts = [];
  const isNewRole = role && !originalRole;
  const isRemovedRole = !role && originalRole;
  const isNewActivity = includesNewActivity(activity, originalActivity);
  const isRemovedActivity = !activity && originalActivity;

  // New activity actions.
  const hasNewActivity =
    activity &&
    actionExists({
      actionMap: impactMap,
      actionType: ActionType.ADD_ACTIVITIES,
      entityType: EntityType.ACTIVITY,
      id: activity.uuid,
    });
  if (activity && !hasNewActivity && isNewActivity) {
    impacts.push(
      addedActivitiesAction({
        activity,
        actionMap: impactMap,
        comparisonScenario,
        scenario,
      })
    );
  }

  // Activity hours added action.
  const hasIncreasedHours =
    activity &&
    actionExists({
      actionMap: impactMap,
      actionType: ActionType.INCREASED_HOURS,
      entityType: EntityType.ACTIVITY,
      id: activity.uuid,
    });

  if (
    !hasIncreasedHours &&
    originalActivity &&
    activity &&
    activity.hours > originalActivity.hours
  ) {
    impacts.push(
      updatedHoursAction({
        actionMap: impactMap,
        actionType: ActionType.INCREASED_HOURS,
        activity,
        comparisonScenario,
        scenario,
      })
    );
  }

  // Activity hours removed action.
  const hasReducedHours =
    activity &&
    actionExists({
      actionMap: impactMap,
      actionType: ActionType.REDUCED_HOURS,
      entityType: EntityType.ACTIVITY,
      id: activity.uuid,
    });

  if (
    !hasReducedHours &&
    originalActivity &&
    activity &&
    activity.hours < originalActivity.hours
  ) {
    impacts.push(
      updatedHoursAction({
        actionMap: impactMap,
        actionType: ActionType.REDUCED_HOURS,
        activity,
        comparisonScenario,
        scenario,
      })
    );
  }

  // New role added.
  const hasNewRole =
    role &&
    actionExists({
      actionMap: impactMap,
      actionType: ActionType.ADD_ROLES,
      entityType: EntityType.ROLE,
      id: role.uuid,
    });
  if (!hasNewRole && isNewRole) {
    impacts.push(
      addedRolesAction({
        actionMap: impactMap,
        comparisonScenario,
        role,
        scenario,
      })
    );
  }

  if (role && originalRole) {
    // Increased FTE.
    const hasIncreasedFTE = actionExists({
      actionMap: impactMap,
      actionType: ActionType.INCREASED_FTE,
      entityType: EntityType.ROLE,
      id: role.uuid,
    });
    if (
      !hasIncreasedFTE &&
      roundFloat(role.__metrics.self.visible.fte) <
        roundFloat(originalRole.__metrics.self.visible.fte)
    ) {
      impacts.push(
        updatedFTEAction({
          actionMap: impactMap,
          actionType: ActionType.INCREASED_FTE,
          comparisonScenario,
          role,
        })
      );
    }

    // Reduced FTE.
    const hasReducedFTE = actionExists({
      actionMap: impactMap,
      actionType: ActionType.REDUCED_FTE,
      entityType: EntityType.ROLE,
      id: role.uuid,
    });
    if (
      !hasReducedFTE &&
      roundFloat(role.__metrics.self.visible.fte) >
        roundFloat(originalRole.__metrics.self.visible.fte)
    ) {
      impacts.push(
        updatedFTEAction({
          actionMap: impactMap,
          actionType: ActionType.REDUCED_FTE,
          comparisonScenario,
          role,
        })
      );
    }

    // Increased budget.
    const hasIncreasedBudget = actionExists({
      actionMap: impactMap,
      actionType: ActionType.INCREASED_BUDGET,
      entityType: EntityType.ROLE,
      id: role.uuid,
    });
    if (
      showBudget &&
      !hasIncreasedBudget &&
      role &&
      originalRole &&
      Math.round(role.__metrics.self.visible.budget) >
        Math.round(originalRole.__metrics.self.visible.budget)
    ) {
      impacts.push(
        updatedBudgetAction({
          actionMap: impactMap,
          actionType: ActionType.INCREASED_BUDGET,
          comparisonScenario,
          role,
          scenario,
        })
      );
    }

    // Reduced budget.
    const hasReducedBudget = actionExists({
      actionMap: impactMap,
      actionType: ActionType.REDUCED_BUDGET,
      entityType: EntityType.ROLE,
      id: role.uuid,
    });
    if (
      showBudget &&
      !hasReducedBudget &&
      role &&
      originalRole &&
      Math.round(role.__metrics.self.visible.budget) <
        Math.round(originalRole.__metrics.self.visible.budget)
    ) {
      impacts.push(
        updatedBudgetAction({
          actionMap: impactMap,
          actionType: ActionType.REDUCED_BUDGET,
          comparisonScenario,
          role,
          scenario,
        })
      );
    }
  }

  const hasRemovedActivity =
    originalActivity &&
    actionExists({
      actionMap: impactMap,
      actionType: ActionType.REMOVE_ACTIVITIES,
      entityType: EntityType.ACTIVITY,
      id: originalActivity.uuid,
    });
  if (!hasRemovedActivity && !activity && originalActivity) {
    impacts.push(
      removedActivitiesAction({
        actionMap: impactMap,
        activity: originalActivity,
        comparisonScenario,
      })
    );
  }

  const hasRemovedRole =
    originalRole &&
    actionExists({
      actionMap: impactMap,
      actionType: ActionType.REMOVE_ROLES,
      entityType: EntityType.ROLE,
      id: originalRole.uuid,
    });
  if (!hasRemovedRole && !role && originalRole) {
    impacts.push(
      removedRolesAction({
        actionMap: impactMap,
        comparisonScenario,
        role: originalRole,
      })
    );
  }

  return impacts;
}
